import React, { Children, useState, useEffect } from 'react'
import { inline } from 'substyle'
import PropTypes from 'prop-types'
import Suggestion from './Suggestion'
import { defaultStyle, getSuggestionHtmlId } from '../utils'


function SuggestionsOverlay({
  id,
  suggestions = {},
  a11ySuggestionsListLabel,
  focusIndex,
  position,
  left,
  right,
  top,
  scrollFocusedIntoView,
  isLoading,
  isOpened,
  onSelect = () => null,
  ignoreAccents,
  containerRef,
  children,
  style,
  customSuggestionsContainer,
  onMouseDown,
  onScroll,
  groupBy,
  onMouseEnter,
}) {
  const [ulElement, setUlElement] = useState(undefined)

  useEffect(() => {
    if (
      !ulElement ||
      !scrollFocusedIntoView ||
      !ulElement.children
    ) {
      return
    }

    // Find the actual focused element by traversing through groups if necessary
    let focusedElement = null;
    let currentIndex = 0;

    const findFocusedElement = (element) => {
      // Check if this is a direct suggestion item
      if (element.getAttribute('role') === 'option') {
        if (currentIndex === focusIndex) {
          focusedElement = element;
          return true;
        }
        currentIndex++;
        return false;
      }

      // If not a suggestion item, check its children
      const children = Array.from(element.children);
      for (const child of children) {
        if (findFocusedElement(child)) return true;
      }
      return false;
    };

    findFocusedElement(ulElement);

    if (!focusedElement && !isLoading) return;

    const itemHeight = focusedElement.offsetHeight;
    const containerHeight = ulElement.offsetHeight;
    const visibleItems = Math.floor(containerHeight / itemHeight) - 1;

    // Calculate which "page" the focused item should be on
    const targetScrollTop = Math.floor(focusIndex / visibleItems) * (visibleItems * itemHeight);

    // Smooth scroll to the calculated position
    ulElement.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth'
    });

  }, [focusIndex, scrollFocusedIntoView, ulElement])


  const renderSuggestions = () => {
    const suggestionsToRender = (
      <ul
        ref={setUlElement}
        id={id}
        role="listbox"
        aria-label={a11ySuggestionsListLabel}
        {...style('list')}
        onScroll={handleScroll}
      >
        {Object.values(suggestions).reduce((accResults, { results, queryInfo }) => {
          if (!groupBy) {
            // If no groupBy, render flat list as before
            return [
              ...accResults,
              ...results.map((result, index) =>
                renderSuggestion(result, queryInfo, accResults.length + index)
              ),
            ]
          }

          // Group results by the groupBy field
          const groups = results.reduce((acc, result) => {
            const groupKey = result[groupBy] || 'Other'
            if (!acc[groupKey]) {
              acc[groupKey] = []
            }
            acc[groupKey].push(result)
            return acc
          }, {})

          // Render groups and their children
          return [
            ...accResults,
            ...Object.entries(groups).map(([groupName, groupResults], groupIndex) => (
              <li key={groupName} {...style('group')}>
                <div {...style('groupHeader')}>{groupName}</div>
                <ul {...style('groupList')}>
                  {groupResults.map((result, index) =>
                    renderSuggestion(
                      result,
                      queryInfo,
                      accResults.length + groupIndex + index
                    )
                  )}
                </ul>
              </li>
            )),
          ]
        }, [])}
      </ul>
    )

    if (customSuggestionsContainer)
      return customSuggestionsContainer(suggestionsToRender)
    return suggestionsToRender
  }

  const renderSuggestion = (result, queryInfo, index) => {
    const isFocused = index === focusIndex
    const { childIndex, query } = queryInfo
    const { renderSuggestion } = Children.toArray(children)[childIndex].props

    return (
      <Suggestion
        role="option"
        query={query}
        index={index}
        focused={isFocused}
        suggestion={result}
        style={style('item')}
        ignoreAccents={ignoreAccents}
        id={getSuggestionHtmlId(id, index)}
        renderSuggestion={renderSuggestion}
        key={`${childIndex}-${getID(result)}`}
        onClick={() => select(result, queryInfo)}
        onMouseEnter={() => handleMouseEnter(index)}
      />
    )
  }

  const renderLoadingIndicator = () => {
    if (!isLoading) {
      return
    }

    return <div style={{
      color: '#2464EB',
      padding: '16px',
      fontSize: '12px',
      textAlign: 'center',
      background: "#FFFFFF",
      paddingBottom: '20px',
      fontFamily: 'Noto Sans',
      borderTop: '1px solid #F5F5F5',
      border: '1px solid var(--border-color)',
      boxShadow: '0 8px 24px rgba(149, 157, 165, 0.2)',
    }}>
      Loading suggestions...
    </div>
  }

  const handleMouseEnter = (index, ev) => {
    if (onMouseEnter) {
      onMouseEnter(index)
    }
  }
  const handleScroll = (ev) => {
    if (onScroll) {
      onScroll(ev)
    }
  }

  const select = (suggestion, queryInfo) => {
    onSelect(suggestion, queryInfo)
  }

  const getID = (suggestion) => {
    if (typeof suggestion === 'string') {
      return suggestion
    }
    return suggestion.id
  }

  if (!isOpened) {
    return null
  }

  return (
    <div
      {...inline({ position: position || 'absolute', left, right, top }, style)}
      onMouseDown={onMouseDown}
      ref={containerRef}
    >
      {renderSuggestions()}
      {renderLoadingIndicator()}
    </div>
  )
}

SuggestionsOverlay.propTypes = {
  id: PropTypes.string.isRequired,
  suggestions: PropTypes.object.isRequired,
  a11ySuggestionsListLabel: PropTypes.string,
  focusIndex: PropTypes.number,
  position: PropTypes.string,
  left: PropTypes.number,
  right: PropTypes.number,
  top: PropTypes.number,
  scrollFocusedIntoView: PropTypes.bool,
  isLoading: PropTypes.bool,
  isOpened: PropTypes.bool.isRequired,
  onSelect: PropTypes.func,
  onScroll: PropTypes.func,
  ignoreAccents: PropTypes.bool,
  customSuggestionsContainer: PropTypes.func,
  containerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current:
        typeof Element === 'undefined'
          ? PropTypes.any
          : PropTypes.instanceOf(Element),
    }),
  ]),
}

const styled = defaultStyle({
  zIndex: 1,
  backgroundColor: 'white',
  marginTop: 14,
  minWidth: 100,

  list: {
    margin: 0,
    padding: 0,
    opacity: 10,
    zIndex: 9999,
    fontSize: `12px`,
    color: `#000000`,
    fontWeight: `700`,
    overflowY: 'auto',
    maxWidth: '250px',
    maxHeight: '250px',
    borderRadius: '4px',
    listStyleType: 'none',
    boxSizing: 'border-box',
    border: '1px solid var(--border-color)',
    boxShadow: '0 8px 24px rgba(149, 157, 165, 0.2)',
  },

  group: {
    margin: 0,
    padding: 0,
  },

  groupHeader: {
    fontWeight: 700,
    color: "#141414",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "8px 10px",
    background: "#FFFFFF",
    fontFamily: "Noto Sans",
    borderBottom: "1px solid #F5F5F5",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },

  groupList: {
    padding: 0,
    fontWeight: 400,
    fontSize: "12px",
    color: "#141414",
    listStyle: "none",
    lineHeight: "16px",
    background: "#FFFFFF",
    fontFamily: "Noto Sans",
  },
  item: {
    padding: 0,
    fontWeight: 400,
    fontSize: "12px",
    color: "#141414",
    listStyle: "none",
    lineHeight: "16px",
    background: "#FFFFFF",
    fontFamily: "Noto Sans",
  }
})

export default styled(SuggestionsOverlay)
